import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getCurrentUser, signIn, signOut, signUp, type User } from '@/lib/auth';
import { getUserSubscriptions, type Subscription } from '@/lib/subscriptions';
import { getUserStats, type UserStats } from '@/lib/progress';
export type { UserStats };

interface AuthContextType {
  user: User | null;
  loading: boolean;
  subscriptions: Subscription[];
  stats: UserStats | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateStats: (stats: UserStats) => void;
  canAccess: (subjectId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      
      if (currentUser) {
        // Load subscriptions and stats
        const [subs, userStats] = await Promise.all([
          getUserSubscriptions(currentUser.$id),
          getUserStats(currentUser.$id),
        ]);
        setSubscriptions(subs);
        setStats(userStats);
      } else {
        setSubscriptions([]);
        setStats(null);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
      setSubscriptions([]);
      setStats(null);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await refreshUser();
      setLoading(false);
    };
    init();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    await signIn(email, password);
    await refreshUser();
  };

  const register = async (email: string, password: string, name: string) => {
    await signUp(email, password, name);
    await signIn(email, password);
  };

  const logout = async () => {
    await signOut();
    setUser(null);
    setSubscriptions([]);
    setStats(null);
  };

  const updateStats = (newStats: UserStats) => setStats(newStats);

  const canAccess = (_subjectId: string): boolean => {
    if (!user) return false;
    // Paid subscriptions use subjectId "global" and unlock everything
    return subscriptions.some(
      s =>
        s.status === 'active' &&
        (!s.endDate || new Date(s.endDate) > new Date()) &&
        (s.plan === 'basic' || s.plan === 'pro'),
    );
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        subscriptions,
        stats,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
        updateStats,
        canAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
